import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StatusBadge from '@/components/base/StatusBadge';
import TopicCard from '@/components/feature/TopicCard';
import { usePageSeo } from '@/hooks/usePageSeo';
import { categoryMap } from '@/mocks/categories';
import { statusMeta, topics } from '@/mocks/topics';
import Timeline from '@/pages/topic-detail/components/Timeline';
import EvidenceList from '@/pages/topic-detail/components/EvidenceList';

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  const structuredData = topic
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: topic.title,
          description: topic.summary,
          datePublished: topic.date,
          dateModified: topic.date,
          inLanguage: 'zh-CN',
          author: { '@type': 'Organization', name: topic.author },
          publisher: { '@type': 'Organization', name: '瓜田求证社' },
          articleSection: categoryMap[topic.categoryId]?.name,
          keywords: topic.tags.join(','),
          mainEntityOfPage: `https://xinnanwangluo.cn/topic/${topic.id}`,
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xinnanwangluo.cn/' },
            { '@type': 'ListItem', position: 2, name: '求证专区', item: 'https://xinnanwangluo.cn/topics' },
            {
              '@type': 'ListItem',
              position: 3,
              name: topic.title,
              item: `https://xinnanwangluo.cn/topic/${topic.id}`,
            },
          ],
        },
      ]
    : undefined;

  usePageSeo({
    title: topic ? `${topic.title}｜求证档案 - 瓜田求证社` : '话题未找到｜瓜田求证社',
    description: topic ? topic.summary : '你访问的求证话题不存在或已被移除。',
    keywords: topic ? topic.tags.join(',') : undefined,
    path: `/topic/${topic?.id ?? ''}`,
    type: 'article',
    structuredData,
  });

  if (!topic) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这条求证档案还没建立</h1>
        <p className="mt-3 text-sm text-foreground-600">你可以回到求证专区，看看我们正在追踪的其他话题。</p>
        <Link
          to="/topics"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回求证专区
        </Link>
      </section>
    );
  }

  const category = categoryMap[topic.categoryId];
  const relatedTopics = topic.related
    .map((relatedId) => topics.find((item) => item.id === relatedId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const meta = statusMeta[topic.status];

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[{ label: '求证专区', to: '/topics' }, { label: category?.name ?? '话题', to: `/topics?category=${topic.categoryId}` }, { label: '求证档案' }]}
          />

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <StatusBadge status={topic.status} size="md" />
            <span className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs text-secondary-800">
              {category?.name}
            </span>
            {topic.tags.map((tag) => (
              <span key={tag} className="rounded-full border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-600">
                #{tag}
              </span>
            ))}
          </div>

          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 text-balance md:text-4xl">
            {topic.title}
          </h1>

          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">{topic.summary}</p>

          <div className="mt-5 flex flex-wrap items-center gap-4 font-label text-xs text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {topic.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              求证时间 {topic.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line"></i>
              讨论热度 {topic.heat.toLocaleString()}
            </span>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-8 md:py-12">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <div className="h-60 w-full overflow-hidden rounded-2xl border border-background-200 sm:h-72 md:h-80">
            <img src={topic.cover} alt={topic.coverAlt} className="h-full w-full object-cover object-top" />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <article className="lg:col-span-8">
              <section aria-labelledby="claim-heading">
                <h2 id="claim-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  传闻原文
                </h2>
                <blockquote className="mt-4 rounded-xl border-l-4 border-primary-400 bg-primary-50 p-5 text-sm leading-relaxed text-primary-900 md:text-base">
                  {topic.claim}
                </blockquote>
              </section>

              <section aria-labelledby="conclusion-heading" className="mt-10">
                <h2 id="conclusion-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  求证结论
                </h2>
                <div className="mt-4 rounded-xl border border-background-200 bg-background-100/60 p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <StatusBadge status={topic.status} size="md" />
                    <span className="font-label text-xs text-foreground-600">{meta.desc}</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">{topic.conclusion}</p>
                  <p className="mt-4 flex items-start gap-2 rounded-md bg-background-50 p-3 text-xs leading-relaxed text-foreground-600">
                    <i className="ri-information-line mt-0.5 text-primary-500"></i>
                    结论基于截至 {topic.date} 可获取的公开材料。若出现新的关键证据，我们会在此页更新并标注修订时间。
                  </p>
                </div>
              </section>

              <section aria-labelledby="timeline-heading" className="mt-10">
                <h2 id="timeline-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  事件时间线
                </h2>
                <div className="mt-5">
                  <Timeline items={topic.timeline} />
                </div>
              </section>

              <section aria-labelledby="evidence-heading" className="mt-10">
                <h2 id="evidence-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  证据清单
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  我们把每条材料按可信度分层列出，其中"反证"指与网传结论相冲突的信息。
                </p>
                <div className="mt-5">
                  <EvidenceList items={topic.evidence} />
                </div>
              </section>

              <section aria-labelledby="analysis-heading" className="mt-10">
                <h2 id="analysis-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  深度分析
                </h2>
                <div className="mt-5 space-y-6">
                  {topic.analysis.map((section) => (
                    <div key={section.heading} className="rounded-xl border border-background-200 bg-background-50 p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-950 md:text-lg">
                        {section.heading}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-foreground-700">{section.body}</p>
                    </div>
                  ))}
                </div>
              </section>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-xl border border-background-200 bg-background-100/60 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">结论速览</h2>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-label text-xs text-foreground-600">求证状态</span>
                      <StatusBadge status={topic.status} withShort />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-label text-xs text-foreground-600">信源条目</span>
                      <span className="font-heading text-sm font-bold text-foreground-950">{topic.evidence.length} 条</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-label text-xs text-foreground-600">时间节点</span>
                      <span className="font-heading text-sm font-bold text-foreground-950">{topic.timeline.length} 个</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-label text-xs text-foreground-600">最近更新</span>
                      <span className="font-heading text-sm font-bold text-foreground-950">{topic.date}</span>
                    </div>
                  </div>
                </div>

                {relatedTopics.length > 0 ? (
                  <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">相关求证</h2>
                    <ul className="mt-4 space-y-3">
                      {relatedTopics.map((item) => (
                        <li key={item.id}>
                          <Link to={`/topic/${item.id}`} className="group flex gap-3">
                            <span className="h-12 w-16 flex-none overflow-hidden rounded-md">
                              <img src={item.cover} alt={item.coverAlt} className="h-full w-full object-cover object-top" />
                            </span>
                            <span className="font-heading text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                              {item.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="mt-5 rounded-xl border border-accent-200 bg-accent-50 p-5">
                  <h2 className="font-heading text-base font-bold text-accent-900">补充线索</h2>
                  <p className="mt-2 text-sm leading-relaxed text-accent-800/90">
                    如果你掌握这个事件的原始材料，欢迎提交给我们，帮助结论更接近事实。
                  </p>
                  <Link
                    to="/about#contact"
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-700"
                  >
                    <i className="ri-send-plane-line"></i>
                    提交线索
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {relatedTopics.length > 0 ? (
            <section aria-labelledby="related-heading" className="mt-14 border-t border-background-200 pt-10">
              <h2 id="related-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                你可能还想求证
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {relatedTopics.map((item) => (
                  <TopicCard key={item.id} topic={item} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </section>
    </>
  );
}